import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CollectionCasePageDto, CollectionCaseService } from '../collection-case.service';
import { CollectionCaseSearchCriteria, CollectionActionCommand, CollectionCaseListItem, CollectionCaseDetailDto } from '../models/collection-case.model';
import { mapApiActionToHistory, mapApiActionToInteraction } from './action-api.mapper';

interface ApiPage<T> { content: T[]; totalElements: number; }
interface ApiDetail extends Omit<CollectionCaseDetailDto, 'dmnDecision'> { dmnDecisionCode?: string; dmnDecisionLabel?: string; actions?: unknown[]; promises?: unknown[]; paymentPlans?: unknown[]; escalations?: unknown[]; }

@Injectable()
export class CollectionCaseApiService extends CollectionCaseService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/cases`;

  getCases(criteria: CollectionCaseSearchCriteria): Observable<CollectionCasePageDto> {
    let params = new HttpParams().set('page', criteria.page).set('size', criteria.size);
    const values: Record<string, unknown> = { ...criteria, query: criteria.debtorName };
    for (const [key, value] of Object.entries(values)) if (value !== undefined && value !== null && key !== 'page' && key !== 'size' && key !== 'sort') params = params.set(key, String(value));
    if (criteria.sort) params = params.set('sort', criteria.sort);
    return this.http.get<ApiPage<CollectionCaseListItem>>(this.base, { params }).pipe(map(page => ({ total: page.totalElements, items: (page.content ?? []).map(mapCaseListItem) })));
  }

  getCaseDetail(caseId: string): Observable<CollectionCaseDetailDto> {
    return this.http.get<ApiDetail>(`${this.base}/${encodeURIComponent(caseId)}`).pipe(map(toDetail));
  }

  performAction(command: CollectionActionCommand): Observable<void> {
    return this.http.post<void>(`${this.base}/${encodeURIComponent(command.caseId)}/actions`, { caseId: command.caseId, type: command.actionType, channel: command.channel, scheduledDate: command.scheduledDate, plannedDate: command.scheduledDate, report: command.report, outcome: command.outcome, durationMinutes: command.durationMinutes, createPromise: command.createPromise, promiseDate: command.promiseDate, promiseAmount: command.promiseAmount });
  }
}

function mapCaseListItem(item: CollectionCaseListItem): CollectionCaseListItem {
  const raw = item as CollectionCaseListItem & { clientName?: string; category?: string; categoryLabel?: string };
  return { ...item, debtorName: raw.debtorName || raw.clientName || 'Client non renseigné', categoryLabel: categoryLabel(raw.categoryLabel || raw.category || raw.categoryCode) };
}

function categoryLabel(value: string | undefined): string {
  const code = String(value ?? '');
  return ({ NEW_CLIENT: 'Nouveau client', BROKEN_PROMISE: 'Promesse non respectée', TODAY_DUE: 'Échéance du jour', DUE_TODAY: 'Échéance du jour', PHONE_REMINDER: 'Rappel téléphone', UNREACHABLE: 'Injoignable / boîte vocale', SEND_SMS: 'Envoi SMS', REMINDER_LETTER: 'Lettre de relance', FOLLOW_UP_LETTER: 'Lettre de relance', NEGOTIATION_DOCUMENT: 'Document de négociation' } as Record<string, string>)[code] ?? (value || '');
}

function toDetail(dto: ApiDetail): CollectionCaseDetailDto {
  const apiActions = dto.actions ?? [];
  const interactions = apiActions.length ? apiActions.map(mapApiActionToInteraction) : (dto.interactions ?? []);
  const history = dto.history?.length ? dto.history : apiActions.map(mapApiActionToHistory);
  return { ...dto, dmnDecision: { segment: '', nextBestAction: dto.dmnDecisionCode ?? '', nextBestActionReason: dto.dmnDecisionLabel ?? '', score: 0, computedAt: dto.lastActionDate }, interactions, history, promises: dto.promises as any, paymentPlan: dto.paymentPlans?.[0] as any, escalations: dto.escalations as any } as CollectionCaseDetailDto;
}
